import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { TenantDataService } from 'app/config/services/tenant-data.service';
import { MaxBrainAuthState } from 'app/projects/auth/src/lib/models/auth.state';
import { LoginAppService } from 'app/projects/auth/src/lib/services/login-app.service';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { firstValueFrom } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

interface ITranslationsQuery {
    language: string;
    translations: {
        [key: string]: string;
    };
}

@Injectable()
export class TranslationsGuard extends UnsubscribeOnDestroy {
    private static _backendTranslations: ITranslationsQuery[] | null = null;

    constructor(
        @Inject(MAXBRAIN_ENVIRONMENT) private _environment: IEnvironment,
        private _http: HttpClient,
        private _store: Store,
        private _translateService: TranslateService,
        private _loginAppService: LoginAppService,
        private _tenantDataService: TenantDataService
    ) {
        super();

        this._translateService.onLangChange.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ lang }) => {
            TranslationsGuard.applyBackendTranslations(this._translateService, lang);
        });
    }

    static cacheBackendTranslations(queries: ITranslationsQuery[]): void {
        TranslationsGuard._backendTranslations = queries.map((query) => {
            const translations = { ...(query.translations || {}) };

            for (const key of Object.keys(translations)) {
                if (translations[key] == null) {
                    translations[key] = '';
                }
            }

            return {
                language: query.language,
                translations,
            };
        });
    }

    static applyBackendTranslations(translateService: TranslateService, language?: string): void {
        if (!TranslationsGuard._backendTranslations) {
            return;
        }

        const queries = language
            ? TranslationsGuard._backendTranslations.filter((query) => query.language === language)
            : TranslationsGuard._backendTranslations;

        for (const query of queries) {
            translateService.setTranslation(query.language, { ...query.translations }, true);
        }
    }

    /**
     * Load tenant translations before route activation. An unauthorized request
     * is retried once after refreshing the access token.
     * @param accessToken
     */
    async fetchTranslation(accessToken: string, retryUnauthorized = true): Promise<boolean> {
        try {
            const queries = await this._fetchTranslations(accessToken);

            if (!queries) {
                throw new Error('Problem to fetch the translations');
            }

            TranslationsGuard.cacheBackendTranslations(queries);
            TranslationsGuard.applyBackendTranslations(this._translateService);

            return true;
        } catch (e) {
            if (retryUnauthorized && e instanceof HttpErrorResponse && e.status === 401 && (await this._loginAppService.fetchAccesToken())) {
                return this.fetchTranslation(this._loginAppService.accessToken, false);
            }

            console.warn('failed to fetch the translation in the translations-guard');
            return true;
        }
    }

    private _fetchTranslations(accessToken: string): Promise<ITranslationsQuery[]> {
        const url = this._tenantDataService.applyAccessTokenBackendUrl(`${this._environment.apiUrl}/translations`, accessToken);

        return firstValueFrom(
            this._http.get<ITranslationsQuery[]>(url, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            })
        );
    }

    /**
     * This guard is use to load the translation files
     * when the access token is valid
     * @param next
     * @param state
     */
    async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        let accessToken = null;

        if (this._loginAppService.hasValidAccessToken()) {
            accessToken = this._loginAppService.accessToken;
        } else {
            accessToken = await firstValueFrom(
                this._store.select(MaxBrainAuthState.getAccessToken).pipe(
                    filter((token) => !!token),
                    take(1)
                )
            );
        }

        return this.fetchTranslation(accessToken);
    }
}
